import type { ListStackableCasesResponse, StackableCase } from '@compass/client/types/call';
import { CallStackRejectionReason } from '@compass/client/types/call';
import { useCreateCallStack } from '@hooks/useCreateCallStack';
import type { StackScope } from '@hooks/useStackableCases';
import { stackableCasesKey, useStackableCases } from '@hooks/useStackableCases';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import {
  dropCase,
  formatSla,
  markStacked,
  rejectionLabel,
  shortCaseId,
  slaTone,
} from '@/utils/callStack';
import { buildRoutePath } from '@/utils/routes';

interface StackCallPanelProps {
  /** The case the call was placed from. Never itself stackable. */
  caseRecordId: string;
  /** The live call. This IS the stack id. Undefined until the call record exists. */
  callId?: string;
  /** Org and program captured when the dialpad opened. */
  scope: StackScope | null;
  /**
   * The payer has answered. Until then the cards can be browsed but not
   * stacked: hermes cuts each stacked case's recording slice from the call's
   * recording start, which does not exist while the call is ringing.
   */
  connected: boolean;
}

const UNSPECIFIED = CallStackRejectionReason.CALL_STACK_REJECTION_REASON_UNSPECIFIED;

const SLA_PILL_CLASS = {
  breached: 'bg-critical text-critical-foreground',
  soon: 'bg-notice text-notice-foreground',
  ok: 'bg-muted text-muted-foreground',
} as const;

const ARROW_CLASS =
  'flex h-7 w-7 items-center justify-center rounded-lg border bg-background text-foreground ' +
  'disabled:cursor-not-allowed disabled:opacity-35 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

/** The full-width slot under a card's details: Start, the confirm, or a status bar. */
const BAR_CLASS = 'mt-3 rounded-[10px] p-[9px] text-center text-[13px]';

const MESSAGE_CLASS = 'py-3.5 text-center text-[13px] text-muted-foreground';

const idOf = (c?: StackableCase) => c?.case_record_id ?? '';

/**
 * The cases this call could also cover, one card at a time.
 *
 * The list refreshes every 5s for the whole live call (GlobalDialpad polls it
 * for the tab's count; see useStackableCases), so a case reassigned away
 * disappears and a newly assigned one appears. The
 * agent stacks a case only through Start → "Are you sure?" → Yes. The server
 * re-checks the case inside its transaction, so a stale card can be refused but
 * never stacked wrongly.
 */
const StackCallPanelComponent = ({
  caseRecordId,
  callId,
  scope,
  connected,
}: StackCallPanelProps) => {
  const queryClient = useQueryClient();
  const query = useStackableCases(scope, caseRecordId, callId);
  const createStack = useCreateCallStack(scope);
  const data = query.data;
  const cases = useMemo(() => data?.cases ?? [], [data]);

  const rootRef = useRef<HTMLDivElement>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [removedCount, setRemovedCount] = useState(0);
  const [failures, setFailures] = useState(0);
  const prevIdsRef = useRef<string[]>([]);

  // With no card picked yet (first open, or back from DTMF Input, which remounts
  // this panel), start on the first case not stacked yet; the first card if all are.
  const firstOpen = Math.max(
    0,
    cases.findIndex(c => !c.in_this_stack),
  );
  const found = cases.findIndex(c => c.case_record_id === currentId);
  const index = found >= 0 ? found : firstOpen;
  const current = cases[index];

  // Track the card by id across refreshes. If it left, land on the case now
  // sitting where it was, and say how many left.
  useEffect(() => {
    const ids = cases.map(idOf);
    const prevIds = prevIdsRef.current;
    const removed = prevIds.filter(id => !ids.includes(id));
    if (removed.length > 0) {
      setRemovedCount(removed.length);
      if (currentId && removed.includes(currentId)) {
        const at = Math.min(prevIds.indexOf(currentId), ids.length - 1);
        setCurrentId(at >= 0 ? ids[at] : null);
        setConfirming(false);
      }
    }
    if (!currentId && ids.length > 0) setCurrentId(ids[firstOpen]);
    prevIdsRef.current = ids;
    // currentId is read, not reacted to: only a new list should move the card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases]);

  // "Couldn't refresh" after two failed polls in a row; any success clears it.
  useEffect(() => {
    if (query.errorUpdatedAt) setFailures(f => f + 1);
  }, [query.errorUpdatedAt]);
  useEffect(() => {
    setFailures(0);
  }, [query.dataUpdatedAt]);

  // Arrow keys and Enter/Esc act on this panel, so it takes focus once per call,
  // as soon as it exists. On a cold open the root only mounts after the first
  // load, so this also runs when loading ends; later refreshes do not steal focus.
  const focusedForRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!callId || query.isLoading || !rootRef.current) return;
    if (focusedForRef.current === callId) return;
    focusedForRef.current = callId;
    rootRef.current.focus();
  }, [callId, query.isLoading]);

  // Opening the confirm puts focus on Yes; closing it returns focus to the panel
  // so the arrows keep working.
  const yesRef = useRef<HTMLButtonElement>(null);
  const wasConfirmingRef = useRef(false);
  useEffect(() => {
    if (confirming) yesRef.current?.focus();
    else if (wasConfirmingRef.current) rootRef.current?.focus();
    wasConfirmingRef.current = confirming;
  }, [confirming]);

  const updateCache = (
    fn: (d: ListStackableCasesResponse | undefined) => ListStackableCasesResponse | undefined,
  ) => {
    if (!callId) return;
    queryClient.setQueryData<ListStackableCasesResponse>(
      stackableCasesKey(caseRecordId, callId),
      fn,
    );
  };

  const go = (delta: number) => {
    if (cases.length === 0) return;
    const next = Math.min(Math.max(index + delta, 0), cases.length - 1);
    setCurrentId(idOf(cases[next]));
    setConfirming(false);
    setRemovedCount(0);
  };

  const nextUnstacked = (fromIndex: number, exceptId: string) => {
    const open = (c: StackableCase) => !c.in_this_stack && c.case_record_id !== exceptId;
    const after = cases.slice(fromIndex + 1).find(open);
    const before = cases.slice(0, fromIndex).find(open);
    return idOf(after ?? before ?? cases[fromIndex]);
  };

  const stack = async () => {
    if (!connected || !callId || !current || createStack.isPending) return;
    const id = idOf(current);
    try {
      const res = await createStack.mutateAsync({ caseRecordId, callId, childCaseRecordIds: [id] });
      setConfirming(false);
      const reason = res.candidates?.[0]?.reason ?? UNSPECIFIED;
      if (reason === UNSPECIFIED) {
        updateCache(d => markStacked(d, id));
        toast.success(`${shortCaseId(id)} stacked on this call`);
        setCurrentId(nextUnstacked(index, id));
      } else {
        toast.error(rejectionLabel(reason));
        // Our own removal, already explained by the toast: keep it out of the
        // "no longer available" note.
        prevIdsRef.current = prevIdsRef.current.filter(x => x !== id);
        const remaining = cases.filter(c => c.case_record_id !== id);
        setCurrentId(idOf(remaining[Math.min(index, remaining.length - 1)]) || null);
        updateCache(d => dropCase(d, id));
      }
      await queryClient.invalidateQueries({ queryKey: stackableCasesKey(caseRecordId, callId) });
      await queryClient.invalidateQueries({ queryKey: ['calls', 'stack'] });
    } catch {
      setConfirming(false);
      toast.error("Couldn't stack, try again");
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (confirming) {
      // Enter on a focused button (No, the link, prev/next) is that control's own
      // action; only Enter on the panel itself confirms.
      if (e.key === 'Enter') {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          void stack();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setConfirming(false);
      }
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  };

  if (!callId) {
    return (
      <p className={MESSAGE_CLASS} data-testid="stack-panel-idle">
        Available once the call starts.
      </p>
    );
  }
  if (query.isLoading) {
    return (
      <p className={MESSAGE_CLASS} data-testid="stack-loading">
        Finding cases…
      </p>
    );
  }

  const action = () => {
    if (!current) return null;
    if (current.in_this_stack) {
      return (
        <div
          className={cn(BAR_CLASS, 'bg-positive font-semibold text-positive-foreground')}
          data-testid="stack-card-locked"
        >
          Stacked
        </div>
      );
    }
    if (!connected) {
      return (
        <div
          className={cn(BAR_CLASS, 'bg-muted text-muted-foreground')}
          data-testid="stack-not-connected"
        >
          Available once the call connects.
        </div>
      );
    }
    if (confirming) {
      return (
        <div
          role="alertdialog"
          aria-label="Confirm stacking"
          className="mt-3 rounded-[10px] border border-destructive bg-critical px-3 py-2.5 text-[13px] motion-safe:animate-in motion-safe:fade-in"
          data-testid="stack-confirm"
        >
          <p className="mb-2 text-critical-foreground">
            <span className="font-bold">Are you sure you want to start stacking?</span>
            <br />
            This creates a human call record on this case and can&apos;t be undone.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="h-9 rounded-[10px] bg-background font-semibold"
              onClick={() => setConfirming(false)}
              data-testid="stack-confirm-no"
            >
              No
            </Button>
            <Button
              variant="destructive"
              className="h-9 rounded-[10px] font-semibold"
              onClick={() => void stack()}
              disabled={createStack.isPending}
              ref={yesRef}
              data-testid="stack-confirm-yes"
            >
              Yes
            </Button>
          </div>
        </div>
      );
    }
    return (
      <Button
        className="mt-3 h-9 w-full rounded-[10px] font-semibold"
        onClick={() => {
          setConfirming(true);
          setRemovedCount(0);
        }}
        data-testid="stack-start"
      >
        Start
      </Button>
    );
  };

  const body = () => {
    if (!data && query.isError) {
      return (
        <p className={MESSAGE_CLASS} data-testid="stack-load-error">
          Couldn&apos;t load cases.
        </p>
      );
    }
    if (data?.call_ended) {
      return (
        <p className={MESSAGE_CLASS} data-testid="stack-call-ended">
          This call has ended.
        </p>
      );
    }
    if (data?.parent_payer_missing) {
      return (
        <p className={MESSAGE_CLASS} data-testid="stack-no-payer">
          This case has no payer, so no cases can be suggested.
        </p>
      );
    }
    if (!current) {
      return (
        <p className={MESSAGE_CLASS} data-testid="stack-empty">
          Nothing to stack on this call.
        </p>
      );
    }
    return (
      <>
        <div
          className={cn(
            'rounded-xl border px-3.5 py-3',
            current.in_this_stack && 'border-positive-foreground',
          )}
          data-testid="stack-card"
        >
          <div className="mb-1.5 flex items-center justify-between gap-2">
            {/* A new tab on purpose: the dialpad overlays the page the agent is on,
                and navigating in place would tear down the live call. */}
            <a
              href={buildRoutePath(
                `/cases/${idOf(current)}`,
                scope?.organizationId ?? '',
                current.program_id ?? '',
              )}
              target="_blank"
              rel="noopener noreferrer"
              title={`${idOf(current)} (open case in a new tab)`}
              aria-label={`Open case ${idOf(current)} in a new tab`}
              className="whitespace-nowrap font-mono text-[13px] font-semibold text-primary hover:underline"
              data-testid="stack-card-link"
            >
              {shortCaseId(idOf(current))}
              <ArrowUpRight className="ml-0.5 inline h-3.5 w-3.5 align-text-bottom" />
            </a>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={ARROW_CLASS}
                onClick={() => go(-1)}
                disabled={index === 0}
                aria-label="Previous case"
                data-testid="stack-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span
                className="min-w-9 text-center text-xs tabular-nums text-muted-foreground"
                data-testid="stack-position"
              >
                {index + 1} / {cases.length}
              </span>
              <button
                type="button"
                className={ARROW_CLASS}
                onClick={() => go(1)}
                disabled={index >= cases.length - 1}
                aria-label="Next case"
                data-testid="stack-next"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Keyed by case so the details slide in as the agent moves. Only the
              details: the arrows must not remount, or a click on one drops focus
              and the arrow keys stop working. */}
          <div
            key={idOf(current)}
            className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-right-3"
          >
            <span
              className={cn(
                'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
                SLA_PILL_CLASS[slaTone(current)],
              )}
              data-testid="stack-card-sla"
            >
              {formatSla(current)}
            </span>

            <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-[3px] text-[13px]">
              <dt className="text-muted-foreground">Program</dt>
              <dd data-testid="stack-card-program">{current.program_name || '—'}</dd>
              <dt className="text-muted-foreground">Payer</dt>
              <dd data-testid="stack-card-payer">{current.payer_name || current.payer_slug}</dd>
            </dl>

            {action()}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="space-y-2.5 text-[13px] outline-none"
      data-testid="stack-panel"
    >
      {body()}
      {removedCount > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="stack-removed-note">
          {removedCount === 1
            ? '1 case is no longer available'
            : `${removedCount} cases are no longer available`}
        </p>
      )}
      {failures >= 2 && (
        <p className="text-xs text-destructive" data-testid="stack-refresh-error">
          Couldn&apos;t refresh
        </p>
      )}
    </div>
  );
};

export const StackCallPanel = memo(StackCallPanelComponent);

/**
 * The Stack call tab's count of cases not stacked yet. Hidden at zero; the tab
 * itself always shows.
 */
export const StackTabCount = ({ count }: { count: number }) =>
  count > 0 ? (
    <span
      className="ml-1.5 inline-block min-w-[18px] rounded-full bg-primary px-1.5 text-center text-[11px] font-bold leading-[18px] text-primary-foreground tabular-nums"
      data-testid="stack-tab-count"
    >
      {count}
    </span>
  ) : null;
